import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProfileStatus {
  profile_completed: boolean;
  completion_percentage: number;
  missing_fields: string[];
  is_approved: boolean;
  user_status: 'ACTIVE' | 'PENDING_APPROVAL' | 'REJECTED' | 'INACTIVE';
}

export const useProfileGuard = () => {
  const { profileStatus, checkProfileStatus, user } = useAuth();
  const [status, setStatus] = useState<ProfileStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      // Si no es cliente, no necesita verificar perfil
      if (user?.role && user.role !== 'client') {
        if (alive) setLoading(false);
        return;
      }

      // Solo verificar una vez por sesión de navegación
      if (hasCheckedRef.current) {
        if (alive) setLoading(false);
        return;
      }

      hasCheckedRef.current = true;
      setLoading(true);

      try {
        await checkProfileStatus();
      } catch (error) {
        console.error('Error in profile guard:', error);
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [checkProfileStatus, user?.role]); // Removido location.pathname para evitar verificaciones constantes

  useEffect(() => {
    if (!profileStatus) return;
    if (user?.role && user.role !== 'client') return;
    setStatus(profileStatus as ProfileStatus);

    const allowedPaths = [
      '/complete-profile',
      '/auth/login',
      '/auth/register',
      '/pending-approval',
      '/account-rejected',
      '/mfa/verify',
      '/mfa/setup',
    ];
    const isAllowedPath = allowedPaths.some((path) => location.pathname.startsWith(path));

    if (!isAllowedPath) {
      if (profileStatus.user_status === 'PENDING_APPROVAL') {
        navigate('/pending-approval', { replace: true });
        return;
      }

      if (profileStatus.user_status === 'REJECTED') {
        navigate('/account-rejected', { replace: true });
        return;
      }

      if (!profileStatus.profile_completed) {
        navigate('/complete-profile', { replace: true });
        return;
      }
    }
  }, [profileStatus, location.pathname, navigate]);

  return {
    status,
    loading,
    refresh: checkProfileStatus,
  };
};
